// De rijen van de weekweergave waarin de dagen horizontaal lopen en de rijen
// links onder elkaar staan (updateverzoek BuurtKlusBedrijf, 2026-09-15).
//
// Twee standen: rijen per medewerker, of rijen per huishouden. De medewerker-
// stand toont ÉLKE actieve medewerker, ook wie niets op de agenda heeft — een
// lege rij is hier de informatie, want daar zit de ruimte. De huishoudenstand
// toont alleen huishoudens met een afspraak: sommige organisaties hebben er
// honderden en die zouden het rooster onleesbaar maken.
//
// Waarom élke taak een bestaande rij MOET hebben: FullCalendar tekent een
// afspraak niet als zijn `resourceId` bij geen enkele rij hoort. Er komt dan
// geen fout en geen waarschuwing — de afspraak is gewoon weg, en dat is niet te
// onderscheiden van een lege week.
// AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading

import { PERSONA_STATUS_LABELS } from '../personas/personaStatusLabels'

/** De twee standen van het rooster. */
const TIMELINE_MODES = {
    personas: 'personas',
    adresses: 'adresses',
}

/** De rij waar alles op belandt wat nergens aan hangt. */
const UNASSIGNED_RESOURCE_ID = '__niet-toegewezen__'

/**
 * De rij voor afspraken van iemand van wie het persoonsdossier niet meer
 * bestaat — alleen zijn id staat nog op de afspraak. Eén gedeelde rij en niet
 * één per id: we kennen hun naam niet, dus meerdere rijen zouden allemaal
 * hetzelfde heten en dat leest als een fout in de app.
 *
 * Iemand die nog wél bestaat maar niet meer actief is, krijgt WEL zijn eigen
 * naam: die is bekend en herkenbaar, en zijn afspraken moeten aan een persoon
 * toe te wijzen zijn. Gemeten bij Vitaminds: 5 van de 15 afspraken op deze rij
 * hoorden bij iemand die gewoon nog bestond.
 */
const UNKNOWN_RESOURCE_ID = '__onbekende-medewerker__'

const UNASSIGNED_TITLE = {
    [TIMELINE_MODES.personas]: 'Niet toegewezen',
    [TIMELINE_MODES.adresses]: 'Zonder huishouden',
}

const UNKNOWN_TITLE = 'Onbekende medewerker'

/** Wat er in de rij staat als een huishouden geen adreslabel meekreeg. */
const ADRESS_FALLBACK_TITLE = 'Huishouden zonder adres'

/**
 * Op welke rij(en) deze taak hoort.
 *
 * Een afspraak met twee teamleden staat op beide rijen; dat is precies wat je
 * in een rooster wilt zien.
 *
 * @param {object} task - Een Tasks-document
 * @param {'personas'|'adresses'} mode - De stand van het rooster
 * @param {Set<string>} [knownIds] - De medewerkers die een eigen rij hebben; wie
 *   daar niet in staat gaat naar de gedeelde rij 'Onbekende medewerker'.
 *   Weglaten betekent: iedereen houdt zijn eigen id.
 * @returns {Array<string>} Altijd minstens één id
 */
const resourceIdsForTask = (task, mode, knownIds) => {
    if (mode === TIMELINE_MODES.adresses) {
        const adressId = task?.AdressID || ''
        return adressId ? [adressId] : [UNASSIGNED_RESOURCE_ID]
    }

    const bekend = (id) => !knownIds || knownIds.has(id)

    const leden = Array.isArray(task?.TeamMembers) ? task.TeamMembers.filter(Boolean) : []
    if (leden.length > 0) {
        // Ontdubbelen: twee niet-actieve leden op dezelfde afspraak zouden de
        // gedeelde rij anders twee keer opleveren.
        return [...new Set(leden.map((id) => (bekend(id) ? id : UNKNOWN_RESOURCE_ID)))]
    }

    // Een roosterblok hangt aan één persoon en draagt geen TeamMembers.
    if (task?.PersonaID) return [bekend(task.PersonaID) ? task.PersonaID : UNKNOWN_RESOURCE_ID]

    return [UNASSIGNED_RESOURCE_ID]
}

/**
 * Of er onder deze taken één zit die nergens aan hangt.
 *
 * @param {Array<object>} tasks
 * @param {'personas'|'adresses'} mode
 * @returns {boolean}
 */
const heeftRij = (tasks, mode, knownIds, rijId) =>
    (tasks || []).some((task) => resourceIdsForTask(task, mode, knownIds).includes(rijId))

const opTitel = (a, b) => String(a.title).localeCompare(String(b.title), 'nl')

/**
 * Alle medewerker-ids die in deze taken voorkomen, ongeacht of ze nog bestaan.
 *
 * @param {Array<object>} tasks
 * @returns {Set<string>}
 */
const idsInTaken = (tasks = []) => {
    const ids = new Set()
    ;(tasks || []).forEach((task) => {
        const leden = Array.isArray(task?.TeamMembers) ? task.TeamMembers.filter(Boolean) : []
        leden.forEach((id) => ids.add(id))
        if (leden.length === 0 && task?.PersonaID) ids.add(task.PersonaID)
    })
    return ids
}

/**
 * De rijen van het rooster.
 *
 * `personas` is de HELE lijst van de organisatie, niet alleen de actieve. Wie
 * actief is krijgt altijd een rij — ook zonder afspraken, want juist die lege
 * rij laat zien waar ruimte is. Wie niet meer actief is krijgt er alleen een
 * als er nog een afspraak van hem staat; anders groeit het rooster vol met
 * iedereen die er ooit gewerkt heeft.
 *
 * @param {object} input
 * @param {'personas'|'adresses'} input.mode - De stand van het rooster
 * @param {Array<{ID: string, Name?: string, Status?: string}>} [input.personas] - Alle medewerkers van de organisatie
 * @param {Array<object>} [input.tasks] - De taken die in beeld kunnen komen
 * @returns {Array<{id: string, title: string, volgorde: number}>} Rijen in weergavevolgorde
 */
const buildTimelineResources = ({ mode, personas = [], tasks = [] }) => {
    const rijen = []

    if (mode === TIMELINE_MODES.adresses) {
        // Op id ontdubbelen: hetzelfde huishouden heeft vaak meer dan één
        // afspraak, en het label komt mee op de taak zelf.
        // AI-CONTEXT: See docs/patterns.md#data-denormalise-label-onto-response
        const gezien = new Map()
        ;(tasks || []).forEach((task) => {
            const id = task?.AdressID || ''
            if (!id || gezien.has(id)) return
            gezien.set(id, task?.AdressLabel || task?.AdressName || ADRESS_FALLBACK_TITLE)
        })
        gezien.forEach((title, id) => rijen.push({ id, title }))
    } else {
        const metAfspraak = idsInTaken(tasks)

        const actief = []
        const nietMeerActief = []

        ;(personas || []).forEach((persona) => {
            if (!persona?.ID) return
            const naam = persona.Name || 'Naamloos'

            if (persona.Status === 'active') {
                actief.push({ id: persona.ID, title: naam })
                return
            }

            // Geen afspraken meer? Dan hoort hij niet in het rooster van deze week.
            if (!metAfspraak.has(persona.ID)) return

            // De status erbij, want anders staat er iemand tussen die niet meer
            // in het team zit zonder dat iets dat zegt. Woord uit de gedeelde
            // lijst, nooit een eigen schrijfwijze.
            const status = PERSONA_STATUS_LABELS[persona.Status]
            nietMeerActief.push({
                id: persona.ID,
                title: status ? `${naam} (${status.toLowerCase()})` : naam,
            })
        })

        actief.sort(opTitel)
        nietMeerActief.sort(opTitel)
        rijen.push(...actief, ...nietMeerActief)
    }

    if (mode === TIMELINE_MODES.adresses) rijen.sort(opTitel)

    const bekend = new Set(rijen.map((r) => r.id))

    // De verzamelrijen horen onderaan, niet ertussen op alfabet. Zonder rij zou
    // een afspraak stil verdwijnen.
    if (mode === TIMELINE_MODES.personas && heeftRij(tasks, mode, bekend, UNKNOWN_RESOURCE_ID)) {
        rijen.push({ id: UNKNOWN_RESOURCE_ID, title: UNKNOWN_TITLE })
    }

    if (heeftRij(tasks, mode, bekend, UNASSIGNED_RESOURCE_ID)) {
        rijen.push({ id: UNASSIGNED_RESOURCE_ID, title: UNASSIGNED_TITLE[mode] })
    }

    // FullCalendar sorteert rijen standaard op `id`, en die van de verzamelrijen
    // beginnen met een liggend streepje — dat sorteert vóór elke letter, dus ze
    // kwamen bovenaan te staan in plaats van onderaan. Een eigen volgnummer plus
    // `resourceOrder="volgorde"` houdt de volgorde die we hier bepalen.
    return rijen.map((rij, index) => ({ ...rij, volgorde: index }))
}

/**
 * De ids die een rij hebben, om `resourceIdsForTask` mee aan te roepen.
 *
 * @param {Array<{id: string}>} resources - Uitkomst van buildTimelineResources
 * @returns {Set<string>}
 */
const knownResourceIds = (resources = []) => new Set(resources.map((r) => r.id))

export {
    TIMELINE_MODES,
    UNASSIGNED_RESOURCE_ID,
    UNKNOWN_RESOURCE_ID,
    ADRESS_FALLBACK_TITLE,
    resourceIdsForTask,
    buildTimelineResources,
    knownResourceIds,
}
